import { JSX, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCRUD } from "../../../api/crud";
import {
  DataGrid,
  GridCallbackDetails,
  GridColDef,
  GridFooterContainer,
  GridPagination,
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  DataGridProps,
  GridColumnResizeParams,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridRowId,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  useMediaQuery,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useCurrentUser } from "../../../api/users";
import { useAuthContext } from "../../../contexts/auth";
import {
  DataGridColumnSetting,
  DataGridGridSettings,
  DataGridSettings,
  DataGridSettingsProfile,
  DataGridSettingsProfileContainer,
  DataGridSortSetting,
} from "../../../types/users";
import styles from "./customDataGrid.module.css";
import { ERROR_SNACKBAR } from "../../../consts";
import MobileDataGridList from "./mobileDataGridList.component";
import CustomModal from "../modal/modal.component";

const COLUMN_WIDTH_SAVE_DELAY = 400;
const LEGACY_PROFILE_ID = "legacy-default";
const LEGACY_MIGRATED_PROFILE_ID = "legacy-migrated";
const DEFAULT_PAGE_SIZE = 25;
// Used to reserve a stable grid height while rows are loading so the skeleton
// doesn't render short and then grow the layout once data arrives. Rough
// estimates - a slight overshoot is preferable to an undershoot here.
const APPROX_ROW_HEIGHT = 64;
const GRID_CHROME_HEIGHT = 160;

interface ResolvedGridProfileSettings {
  profiles: Record<string, DataGridSettingsProfile>;
  activeProfileId: string | null;
  draftProfileId: string | null;
  draftColumns: Record<string, DataGridColumnSetting>;
  draftSortModel: GridSortModel;
  draftPageSize: number;
}

interface IndexedGridColumn {
  column: GridColDef;
  index: number;
}

const cloneColumnSettings = (
  source?: Record<string, DataGridColumnSetting>
): Record<string, DataGridColumnSetting> => {
  if (!source) {
    return {};
  }

  return Object.entries(source).reduce<Record<string, DataGridColumnSetting>>(
    (acc, [field, value]) => {
      if (!value || typeof value !== "object") {
        return acc;
      }

      const next: DataGridColumnSetting = {};

      if (typeof value.width === "number") {
        next.width = value.width;
      }

      if (typeof value.hidden === "boolean") {
        next.hidden = value.hidden;
      }

      if (typeof value.order === "number") {
        next.order = value.order;
      }

      if (
        next.width !== undefined ||
        next.hidden !== undefined ||
        next.order !== undefined
      ) {
        acc[field] = next;
      }

      return acc;
    },
    {}
  );
};

const normalizedComparableSettings = (
  source: Record<string, DataGridColumnSetting>
) => {
  return Object.keys(source)
    .sort()
    .reduce<Record<string, DataGridColumnSetting>>((acc, key) => {
      const value = source[key];
      const normalized: DataGridColumnSetting = {};

      if (typeof value?.width === "number") {
        normalized.width = value.width;
      }

      if (typeof value?.hidden === "boolean") {
        normalized.hidden = value.hidden;
      }

      if (typeof value?.order === "number") {
        normalized.order = value.order;
      }

      if (
        normalized.width !== undefined ||
        normalized.hidden !== undefined ||
        normalized.order !== undefined
      ) {
        acc[key] = normalized;
      }

      return acc;
    }, {});
};

const normalizePageSize = (value?: number) =>
  typeof value === "number" && value > 0 ? value : DEFAULT_PAGE_SIZE;

// Namespaced by gridSettingsKey so multiple grids rendered on the same page
// (e.g. dashboard breakdown grids) don't collide on the same query param.
const getPageParamName = (gridSettingsKey?: string) =>
  gridSettingsKey ? `page_${gridSettingsKey.toLowerCase()}` : undefined;

const parsePageParam = (value: string | null) => {
  const parsed = value !== null ? parseInt(value, 10) : 0;
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
};

const areColumnSettingsEqual = (
  left: Record<string, DataGridColumnSetting>,
  right: Record<string, DataGridColumnSetting>
) =>
  JSON.stringify(normalizedComparableSettings(left)) ===
  JSON.stringify(normalizedComparableSettings(right));

const normalizeSortModel = (
  source?: GridSortModel | ReadonlyArray<DataGridSortSetting>
): DataGridSortSetting[] =>
  Array.isArray(source)
    ? source
        .filter(
          (item) =>
            item &&
            typeof item.field === "string" &&
            (item.sort === "asc" || item.sort === "desc")
        )
        .map((item) => ({ field: item.field, sort: item.sort }))
    : [];

const toGridSortModel = (
  source?: GridSortModel | ReadonlyArray<DataGridSortSetting>
): GridSortModel =>
  normalizeSortModel(source).map((item) => ({
    field: item.field,
    sort: item.sort,
  }));

const areSortModelsEqual = (left: GridSortModel, right: GridSortModel) =>
  JSON.stringify(normalizeSortModel(left)) === JSON.stringify(normalizeSortModel(right));

const areVisibilityModelsEqual = (
  left: GridColumnVisibilityModel,
  right: GridColumnVisibilityModel
) => JSON.stringify(left) === JSON.stringify(right);

const areColumnsLayoutEqual = (left: GridColDef[], right: GridColDef[]) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((column, index) => {
    const rightColumn = right[index];

    return (
      column.field === rightColumn.field &&
      column.width === rightColumn.width &&
      column.headerName === rightColumn.headerName &&
      column.renderHeader === rightColumn.renderHeader &&
      column.renderCell === rightColumn.renderCell &&
      column.valueGetter === rightColumn.valueGetter &&
      column.valueFormatter === rightColumn.valueFormatter
    );
  });
};

const areResolvedProfileSettingsEqual = (
  left: ResolvedGridProfileSettings,
  right: ResolvedGridProfileSettings
) => JSON.stringify(toProfileContainer(left)) === JSON.stringify(toProfileContainer(right));

const isProfileContainer = (
  settings: DataGridGridSettings | undefined
): settings is DataGridSettingsProfileContainer =>
  Boolean(
    settings &&
      typeof settings === "object" &&
      "profiles" in settings
  );

const resolveGridProfileSettings = (
  rawSettings: DataGridGridSettings | undefined,
  defaultProfileName: string,
  importedProfileName: string,
  defaultPageSize: number
): ResolvedGridProfileSettings => {
  if (isProfileContainer(rawSettings)) {
    const nextProfiles = Object.values(rawSettings.profiles ?? {}).reduce<
      Record<string, DataGridSettingsProfile>
    >((acc, profile) => {
      if (!profile?.id) {
        return acc;
      }

      acc[profile.id] = {
        id: profile.id,
        name: profile.name || defaultProfileName,
        columns: cloneColumnSettings(profile.columns),
        sort_model: normalizeSortModel(profile.sort_model),
        page_size: normalizePageSize(profile.page_size),
      };

      return acc;
    }, {});

    // Keep built-in default profile immutable and empty so user can always revert.
    nextProfiles[LEGACY_PROFILE_ID] = {
      id: LEGACY_PROFILE_ID,
      name: defaultProfileName,
      columns: {},
      sort_model: [],
      page_size: defaultPageSize,
    };

    const activeProfileId =
      rawSettings.active_profile_id && nextProfiles[rawSettings.active_profile_id]
        ? rawSettings.active_profile_id
        : LEGACY_PROFILE_ID;

    const baseDraftColumns = activeProfileId
      ? nextProfiles[activeProfileId]?.columns
      : undefined;
    const baseDraftSortModel = activeProfileId
      ? nextProfiles[activeProfileId]?.sort_model
      : undefined;
    const baseDraftPageSize = activeProfileId
      ? nextProfiles[activeProfileId]?.page_size
      : undefined;

    return {
      profiles: nextProfiles,
      activeProfileId,
      draftProfileId: rawSettings.draft_profile_id ?? activeProfileId,
      draftColumns: cloneColumnSettings(rawSettings.draft_columns ?? baseDraftColumns),
      draftSortModel: toGridSortModel(rawSettings.draft_sort_model ?? baseDraftSortModel),
      draftPageSize: normalizePageSize(rawSettings.draft_page_size ?? baseDraftPageSize),
    };
  }

  const legacyColumns = cloneColumnSettings(
    rawSettings as Record<string, DataGridColumnSetting> | undefined
  );

  const defaultProfile: DataGridSettingsProfile = {
    id: LEGACY_PROFILE_ID,
    name: defaultProfileName,
    columns: {},
    sort_model: [],
    page_size: defaultPageSize,
  };

  const hasLegacyColumns = Object.keys(legacyColumns).length > 0;
  const importedProfile: DataGridSettingsProfile | null = hasLegacyColumns
    ? {
      id: LEGACY_MIGRATED_PROFILE_ID,
      name: importedProfileName,
      columns: legacyColumns,
      sort_model: [],
    }
    : null;

  const activeProfileId = importedProfile ? LEGACY_MIGRATED_PROFILE_ID : LEGACY_PROFILE_ID;

  return {
    profiles: {
      [LEGACY_PROFILE_ID]: defaultProfile,
      ...(importedProfile
        ? {
          [LEGACY_MIGRATED_PROFILE_ID]: importedProfile,
        }
        : {}),
    },
    activeProfileId,
    draftProfileId: activeProfileId,
    draftColumns: cloneColumnSettings(
      importedProfile ? importedProfile.columns : defaultProfile.columns
    ),
    draftSortModel: toGridSortModel(
      importedProfile ? importedProfile.sort_model : defaultProfile.sort_model
    ),
    draftPageSize: normalizePageSize(
      importedProfile ? importedProfile.page_size : defaultProfile.page_size
    ),
  };
};

const toProfileContainer = (
  settings: ResolvedGridProfileSettings
): DataGridSettingsProfileContainer => ({
  active_profile_id: settings.activeProfileId,
  draft_profile_id: settings.draftProfileId,
  draft_columns: cloneColumnSettings(settings.draftColumns),
  draft_sort_model: normalizeSortModel(settings.draftSortModel),
  draft_page_size: normalizePageSize(settings.draftPageSize),
  profiles: Object.values(settings.profiles).reduce<
    Record<string, DataGridSettingsProfile>
  >((acc, profile) => {
    acc[profile.id] = {
      id: profile.id,
      name: profile.name,
      columns: cloneColumnSettings(profile.columns),
      sort_model: normalizeSortModel(profile.sort_model),
      page_size: normalizePageSize(profile.page_size),
    };
    return acc;
  }, {}),
});

const applyStoredColumnWidths = (
  sourceColumns: GridColDef[],
  storedSettings?: Record<string, DataGridColumnSetting>
) =>
  sourceColumns
    .map<IndexedGridColumn>((column, index) => ({
      column,
      index,
    }))
    .sort((left, right) => {
      const leftOrder = storedSettings?.[left.column.field]?.order;
      const rightOrder = storedSettings?.[right.column.field]?.order;
      const hasLeftOrder = typeof leftOrder === "number";
      const hasRightOrder = typeof rightOrder === "number";

      if (hasLeftOrder && hasRightOrder) {
        if (leftOrder === rightOrder) {
          return left.index - right.index;
        }

        return leftOrder - rightOrder;
      }

      if (hasLeftOrder) {
        return -1;
      }

      if (hasRightOrder) {
        return 1;
      }

      return left.index - right.index;
    })
    .map(({ column }) => {
    const storedWidth = storedSettings?.[column.field]?.width;

    if (storedWidth === undefined) {
      return column;
    }

    return {
      ...column,
      width: storedWidth,
    };
  });

const getColumnsWithUpdatedOrder = (
  sourceColumns: GridColDef[],
  movedField: string,
  targetIndex: number
): GridColDef[] => {
  const currentIndex = sourceColumns.findIndex(
    (column) => column.field === movedField
  );

  if (currentIndex < 0) {
    return sourceColumns;
  }

  const boundedTargetIndex = Math.max(
    0,
    Math.min(targetIndex, sourceColumns.length - 1)
  );

  if (currentIndex === boundedTargetIndex) {
    return sourceColumns;
  }

  const nextColumns = [...sourceColumns];
  const [movedColumn] = nextColumns.splice(currentIndex, 1);
  nextColumns.splice(boundedTargetIndex, 0, movedColumn);

  return nextColumns;
};

const getStoredColumnVisibilityModel = (
  sourceColumns: GridColDef[],
  storedSettings?: Record<string, DataGridColumnSetting>,
  defaultHiddenFields?: string[]
): GridColumnVisibilityModel =>
  sourceColumns.reduce<GridColumnVisibilityModel>((acc, column) => {
    const hidden = storedSettings?.[column.field]?.hidden;

    if (hidden === true || (hidden === undefined && defaultHiddenFields?.includes(column.field))) {
      acc[column.field] = false;
    }

    return acc;
  }, {});

interface CustomDataGridProps {
  icon?: JSX.Element;
  title?: string;
  headerActions?: ReactNode;
  url: string;
  gridSettingsKey?: string;
  columns: GridColDef[];
  onRowClick?: (id: string, row?: any) => void;
  refreshEventName?: string | string[];
  dataGridProps?: Partial<DataGridProps>;
  initialFilterModel?: GridFilterModel;
  initialSortModel?: GridSortModel;
  /**
   * Fields hidden by default (e.g. a column that should only be reachable via
   * the grid's own Filters panel, not shown/pinned in the table). Only applies
   * when the user has no stored visibility preference for that field.
   */
  defaultHiddenFields?: string[];
  /**
   * Static scope params (e.g. technician_id, start_date) sent on every request
   * regardless of the grid's own column filters. Kept out of GridFilterModel
   * because the (Community) DataGrid forces disableMultipleColumnsFiltering,
   * which silently truncates filterModel.items to a single entry as soon as
   * the user touches the built-in filter UI - putting scope params there
   * means they get wiped out by the user's own filtering.
   */
  extraParams?: Record<string, string | number | null | undefined>;
  disableServerQuery?: boolean;
  /**
   * Initial page size to use when the user has no stored grid profile yet
   * (e.g. a fresh `gridSettingsKey`, or none at all). Defaults to
   * `DEFAULT_PAGE_SIZE` (25). Once the user changes the page size, their
   * choice is persisted (per `gridSettingsKey`) and takes over from here.
   */
  defaultPageSize?: number;
  /**
   * Adds checkbox selection, controlled by the caller. Rows go in and out
   * whole rather than as ids: the selection survives paging, and the caller
   * still needs a row's data once it has paged out of the grid.
   */
  rowSelection?: {
    selectedRows: any[];
    onChange: (rows: any[]) => void;
  };
}

export default function CustomDataGrid({
  icon,
  title,
  headerActions,
  url,
  gridSettingsKey,
  columns: initialColumns,
  onRowClick,
  refreshEventName,
  dataGridProps,
  initialFilterModel,
  initialSortModel,
  defaultHiddenFields,
  extraParams,
  disableServerQuery,
  defaultPageSize,
  rowSelection,
}: CustomDataGridProps) {
  const isMediumUp = useMediaQuery("(min-width:900px)");
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser, setCurrentUser } = useAuthContext();
  const { update: updateCurrentUser } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParamName = getPageParamName(gridSettingsKey);
  const getResolvedProfileSettings = useCallback(
    () =>
      resolveGridProfileSettings(
        gridSettingsKey
          ? (currentUser?.datagrid_settings?.[gridSettingsKey] as DataGridGridSettings | undefined)
          : undefined,
        t("Default"),
        t("Imported profile"),
        defaultPageSize ?? DEFAULT_PAGE_SIZE
      ),
    [currentUser?.datagrid_settings, gridSettingsKey, t, defaultPageSize]
  );
  const [paginationModel, setPaginationModel] = useState(() => ({
    page: pageParamName ? parsePageParam(searchParams.get(pageParamName)) : 0,
    pageSize: getResolvedProfileSettings().draftPageSize,
  }));
  const [sortModel, setSortModel] = useState<GridSortModel>(
    () => {
      const resolvedSettings = getResolvedProfileSettings();
      return toGridSortModel(resolvedSettings.draftSortModel ?? initialSortModel ?? []);
    }
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    () => initialFilterModel ?? { items: [] }
  );
  const queryOptions = useMemo(
    () => ({ ...paginationModel, sortModel, filterModel, extraParams }),
    [paginationModel, sortModel, filterModel, extraParams]
  );
  const { query, loading } = useCRUD(url);
  const [results, setResults] = useState<any[] | undefined>();
  const [rowCount, setRowCount] = useState<number>(0);
  const [profileSettings, setProfileSettings] =
    useState<ResolvedGridProfileSettings>(getResolvedProfileSettings);
  const [columns, setColumns] = useState<GridColDef[]>(() =>
    applyStoredColumnWidths(
      initialColumns,
      getResolvedProfileSettings().draftColumns
    )
  );
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() =>
      getStoredColumnVisibilityModel(
        initialColumns,
        getResolvedProfileSettings().draftColumns,
        defaultHiddenFields
      )
    );
  const [pendingDatagridSettings, setPendingDatagridSettings] =
    useState<DataGridSettings | null>(null);
  const pendingDatagridSettingsRef = useRef<DataGridSettings | null>(null);
  const [createProfileModalOpen, setCreateProfileModalOpen] = useState(false);
  const [profileNameInput, setProfileNameInput] = useState("");
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestVersionRef = useRef(0);
  const appliedFilterModelRef = useRef(initialFilterModel);
  const appliedExtraParamsRef = useRef(extraParams);

  const selectedProfile = profileSettings.activeProfileId
    ? profileSettings.profiles[profileSettings.activeProfileId]
    : undefined;
  const selectedProfileColumns = selectedProfile?.columns ?? {};
  const selectedProfileSortModel = toGridSortModel(selectedProfile?.sort_model);
  const selectedProfilePageSize = normalizePageSize(selectedProfile?.page_size);
  const hasUnsavedChanges =
    !areColumnSettingsEqual(profileSettings.draftColumns, selectedProfileColumns) ||
    !areSortModelsEqual(profileSettings.draftSortModel, selectedProfileSortModel) ||
    profileSettings.draftPageSize !== selectedProfilePageSize;

  const profileOptions = useMemo(
    () => Object.values(profileSettings.profiles),
    [profileSettings.profiles]
  );

  const queueDatagridSettingsUpdate = useCallback(
    (updater: (previous: ResolvedGridProfileSettings) => ResolvedGridProfileSettings) => {
      if (!gridSettingsKey) {
        return;
      }

      setProfileSettings((previous) => {
        const next = updater(previous);

        setPendingDatagridSettings((pendingSettings) => {
          const baseSettings = pendingSettings ?? currentUser?.datagrid_settings ?? {};

          return {
            ...baseSettings,
            [gridSettingsKey]: toProfileContainer(next),
          };
        });

        return next;
      });
    },
    [currentUser?.datagrid_settings, gridSettingsKey]
  );

  const persistDatagridSettings = useCallback(
    async (settings: DataGridSettings) => {
      const response = await updateCurrentUser({
        datagrid_settings: settings,
      });

      if (response?.success && response?.data) {
        setCurrentUser(response.data);
      }
    },
    [setCurrentUser, updateCurrentUser]
  );

  useEffect(() => {
    const resolved = getResolvedProfileSettings();
    const resolvedColumns = applyStoredColumnWidths(initialColumns, resolved.draftColumns);
    const resolvedVisibilityModel = getStoredColumnVisibilityModel(
      initialColumns,
      resolved.draftColumns,
      defaultHiddenFields
    );
    const resolvedSortModel = toGridSortModel(
      resolved.draftSortModel ?? initialSortModel ?? []
    );
    const resolvedPageSize = normalizePageSize(resolved.draftPageSize);

    setProfileSettings((previous) =>
      areResolvedProfileSettingsEqual(previous, resolved) ? previous : resolved
    );
    setColumns((previous) =>
      areColumnsLayoutEqual(previous, resolvedColumns) ? previous : resolvedColumns
    );
    setColumnVisibilityModel((previous) =>
      areVisibilityModelsEqual(previous, resolvedVisibilityModel)
        ? previous
        : resolvedVisibilityModel
    );
    setSortModel((previous) =>
      areSortModelsEqual(previous, resolvedSortModel)
        ? previous
        : resolvedSortModel
    );
    // Only reset to page 0 when the page size genuinely changed (e.g. a
    // profile with a different page size just loaded). This effect also
    // re-fires after our own debounced settings autosave round-trips through
    // the server and refreshes currentUser - without this guard, it would
    // unconditionally snap the user back to page 0 while they're browsing,
    // firing a second, page-0 request right after the one for the page they
    // just navigated to.
    setPaginationModel((previous) =>
      previous.pageSize === resolvedPageSize
        ? previous
        : { page: 0, pageSize: resolvedPageSize }
    );
  }, [getResolvedProfileSettings, initialColumns, initialSortModel, defaultHiddenFields]);

  const previousActiveProfileIdRef = useRef(profileSettings.activeProfileId);

  useEffect(() => {
    // Only react to an actual profile switch (e.g. handleRemoveProfile falling
    // back to another profile). Re-running this on every `profiles` reference
    // change - which also happens after the debounced autosave round-trips
    // through the server and refreshes currentUser - would stomp the user's
    // in-flight draft (e.g. a column they just hid) with the last *saved*
    // profile state, making the change appear to revert a moment later.
    if (previousActiveProfileIdRef.current === profileSettings.activeProfileId) {
      return;
    }
    previousActiveProfileIdRef.current = profileSettings.activeProfileId;

    const activeProfileColumns = profileSettings.activeProfileId
      ? profileSettings.profiles[profileSettings.activeProfileId]?.columns
      : undefined;

    if (!activeProfileColumns) {
      return;
    }

    setColumns(applyStoredColumnWidths(initialColumns, activeProfileColumns));
    setColumnVisibilityModel(
      getStoredColumnVisibilityModel(initialColumns, activeProfileColumns, defaultHiddenFields)
    );
  }, [initialColumns, profileSettings.activeProfileId, profileSettings.profiles, defaultHiddenFields]);

  const handleProfileChange = (event: SelectChangeEvent<string>) => {
    const nextProfileId = event.target.value;

    const nextProfile = profileSettings.profiles[nextProfileId];

    if (!nextProfile) {
      return;
    }

    setColumns(applyStoredColumnWidths(initialColumns, nextProfile.columns));
    setColumnVisibilityModel(
      getStoredColumnVisibilityModel(initialColumns, nextProfile.columns, defaultHiddenFields)
    );
    setSortModel(toGridSortModel(nextProfile.sort_model));
    setPaginationModel({ page: 0, pageSize: normalizePageSize(nextProfile.page_size) });

    queueDatagridSettingsUpdate((previous) => ({
      ...previous,
      activeProfileId: nextProfileId,
      draftProfileId: nextProfileId,
      draftColumns: cloneColumnSettings(nextProfile.columns),
      draftSortModel: toGridSortModel(nextProfile.sort_model),
      draftPageSize: normalizePageSize(nextProfile.page_size),
    }));
  };

  const openCreateProfileModal = () => {
    setProfileNameInput("");
    setCreateProfileModalOpen(true);
  };

  const handleSaveProfile = () => {
    if (
      !hasUnsavedChanges ||
      !profileSettings.activeProfileId ||
      profileSettings.activeProfileId === LEGACY_PROFILE_ID
    ) {
      return;
    }

    queueDatagridSettingsUpdate((previous) => {
      const activeProfileId = previous.activeProfileId;

      if (
        !activeProfileId ||
        activeProfileId === LEGACY_PROFILE_ID ||
        !previous.profiles[activeProfileId]
      ) {
        return previous;
      }

      const nextProfile: DataGridSettingsProfile = {
        ...previous.profiles[activeProfileId],
        columns: cloneColumnSettings(previous.draftColumns),
        sort_model: normalizeSortModel(previous.draftSortModel),
        page_size: normalizePageSize(previous.draftPageSize),
      };

      return {
        ...previous,
        draftProfileId: activeProfileId,
        draftColumns: cloneColumnSettings(nextProfile.columns),
        draftSortModel: toGridSortModel(nextProfile.sort_model),
        draftPageSize: normalizePageSize(nextProfile.page_size),
        profiles: {
          ...previous.profiles,
          [activeProfileId]: nextProfile,
        },
      };
    });

    enqueueSnackbar(t("Profile updated"));
  };

  const handleCreateProfile = () => {
    const profileName = profileNameInput.trim();

    if (!profileName) {
      enqueueSnackbar(t("Profile name is required"), ERROR_SNACKBAR);
      return;
    }

    queueDatagridSettingsUpdate((previous) => {
      const nextProfileId = `profile-${Date.now()}`;

      return {
        ...previous,
        activeProfileId: nextProfileId,
        draftProfileId: nextProfileId,
        profiles: {
          ...previous.profiles,
          [nextProfileId]: {
            id: nextProfileId,
            name: profileName,
            columns: cloneColumnSettings(previous.draftColumns),
            sort_model: normalizeSortModel(previous.draftSortModel),
            page_size: normalizePageSize(previous.draftPageSize),
          },
        },
      };
    });

    setCreateProfileModalOpen(false);
    enqueueSnackbar(t("Profile created"));
  };

  const handleRemoveProfile = () => {
    if (!profileSettings.activeProfileId || hasUnsavedChanges) {
      return;
    }

    queueDatagridSettingsUpdate((previous) => {
      const removedProfileId = previous.activeProfileId;

      if (
        !removedProfileId ||
        removedProfileId === LEGACY_PROFILE_ID ||
        !previous.profiles[removedProfileId]
      ) {
        return previous;
      }

      const nextProfiles = { ...previous.profiles };
      delete nextProfiles[removedProfileId];

      const remainingProfileIds = Object.keys(nextProfiles);

      if (remainingProfileIds.length === 0) {
        return previous;
      }

      const nextActiveProfileId =
        previous.draftProfileId &&
        previous.draftProfileId !== removedProfileId &&
        nextProfiles[previous.draftProfileId]
          ? previous.draftProfileId
          : remainingProfileIds[0];

      return {
        ...previous,
        profiles: nextProfiles,
        activeProfileId: nextActiveProfileId,
        draftProfileId: nextActiveProfileId,
        draftColumns: cloneColumnSettings(nextProfiles[nextActiveProfileId]?.columns),
        draftSortModel: toGridSortModel(nextProfiles[nextActiveProfileId]?.sort_model),
        draftPageSize: normalizePageSize(nextProfiles[nextActiveProfileId]?.page_size),
      };
    });

    enqueueSnackbar(t("Profile removed"));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.field === params.colDef.field
          ? { ...col, width: params.width }
          : col
      )
    );

    if (!gridSettingsKey) {
      return;
    }

    queueDatagridSettingsUpdate((previous) => {
      const nextDraftColumns = cloneColumnSettings(previous.draftColumns);
      const existingColumnSettings = nextDraftColumns[params.colDef.field] ?? {};
      const nextColumnSettings = {
        ...existingColumnSettings,
        width: params.width,
      };

      if (
        nextColumnSettings.width === undefined &&
        nextColumnSettings.hidden === undefined
      ) {
        delete nextDraftColumns[params.colDef.field];
      } else {
        nextDraftColumns[params.colDef.field] = nextColumnSettings;
      }

      return {
        ...previous,
        draftColumns: nextDraftColumns,
      };
    });
  };

  const handleColumnVisibilityModelChange = (
    visibilityModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(visibilityModel);

    if (!gridSettingsKey) {
      return;
    }

    queueDatagridSettingsUpdate((previous) => {
      const nextDraftColumns = cloneColumnSettings(previous.draftColumns);

      initialColumns.forEach((column) => {
        const columnWidth =
          columns.find((item) => item.field === column.field)?.width ??
          nextDraftColumns[column.field]?.width;
        const hidden = (visibilityModel[column.field] ?? true) === false;
        const nextColumnSettings: DataGridColumnSetting = {};

        if (typeof columnWidth === "number") {
          nextColumnSettings.width = columnWidth;
        }

        if (typeof nextDraftColumns[column.field]?.order === "number") {
          nextColumnSettings.order = nextDraftColumns[column.field].order;
        }

        if (hidden) {
          nextColumnSettings.hidden = true;
        } else if (defaultHiddenFields?.includes(column.field)) {
          // Persist an explicit "shown" flag for columns hidden by default,
          // otherwise the missing setting resolves back to hidden on resync.
          nextColumnSettings.hidden = false;
        }

        if (
          nextColumnSettings.width === undefined &&
          nextColumnSettings.hidden === undefined &&
          nextColumnSettings.order === undefined
        ) {
          delete nextDraftColumns[column.field];
          return;
        }

        nextDraftColumns[column.field] = nextColumnSettings;
      });

      return {
        ...previous,
        draftColumns: nextDraftColumns,
      };
    });
  };

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    setColumns((previousColumns) => {
      const reorderedColumns = getColumnsWithUpdatedOrder(
        previousColumns,
        params.column.field,
        params.targetIndex
      );

      if (!gridSettingsKey || reorderedColumns === previousColumns) {
        return reorderedColumns;
      }

      queueDatagridSettingsUpdate((previous) => {
        const nextDraftColumns = cloneColumnSettings(previous.draftColumns);

        reorderedColumns.forEach((column, index) => {
          const existingColumnSettings = nextDraftColumns[column.field] ?? {};
          nextDraftColumns[column.field] = {
            ...existingColumnSettings,
            order: index,
          };
        });

        return {
          ...previous,
          draftColumns: nextDraftColumns,
        };
      });

      return reorderedColumns;
    });
  };

  useEffect(() => {
    pendingDatagridSettingsRef.current = pendingDatagridSettings;
  }, [pendingDatagridSettings]);

  useEffect(() => {
    if (!pendingDatagridSettings || !gridSettingsKey) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      const settingsToSave = pendingDatagridSettings;

      void persistDatagridSettings(settingsToSave);

      setPendingDatagridSettings((currentPending) =>
        currentPending === settingsToSave ? null : currentPending
      );
    }, COLUMN_WIDTH_SAVE_DELAY);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [gridSettingsKey, pendingDatagridSettings, persistDatagridSettings]);

  useEffect(() => {
    return () => {
      if (!gridSettingsKey || !pendingDatagridSettingsRef.current) {
        return;
      }

      void persistDatagridSettings(pendingDatagridSettingsRef.current);
    };
  }, [gridSettingsKey, persistDatagridSettings]);

  // Keep the current page mirrored into the URL (replacing, not pushing, so
  // paging around doesn't spam browser history) so that navigating away (e.g.
  // to a row's detail page) and back via a breadcrumb or the browser's
  // back/forward button restores the page the user was on.
  useEffect(() => {
    if (!pageParamName) {
      return;
    }

    const desiredValue = paginationModel.page > 0 ? String(paginationModel.page) : null;
    const currentValue = searchParams.get(pageParamName);

    if (currentValue === desiredValue) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);

    if (desiredValue === null) {
      nextParams.delete(pageParamName);
    } else {
      nextParams.set(pageParamName, desiredValue);
    }

    setSearchParams(nextParams, { replace: true });
  }, [pageParamName, paginationModel.page, searchParams, setSearchParams]);

  // The other direction: if the URL's page param changes without us having
  // set paginationModel first (browser back/forward while the grid stays
  // mounted), adopt it.
  useEffect(() => {
    if (!pageParamName) {
      return;
    }

    const urlPage = parsePageParam(searchParams.get(pageParamName));

    setPaginationModel((previous) =>
      previous.page === urlPage ? previous : { ...previous, page: urlPage }
    );
  }, [pageParamName, searchParams]);

  const loadData = useCallback(async () => {
    if (disableServerQuery) {
      return;
    }

    const requestVersion = ++requestVersionRef.current;
    const data = await query(queryOptions);
    const nextResults = data?.results;
    const nextCount = data?.count;

    // Ignore stale responses so quick-filter/sort updates always show latest data.
    if (requestVersion !== requestVersionRef.current) {
      return;
    }

    if (Array.isArray(nextResults)) {
      setResults(nextResults);
    }

    if (typeof nextCount === "number") {
      setRowCount(nextCount);
    }
  }, [disableServerQuery, query, queryOptions]);

  useEffect(() => {
    if (disableServerQuery) {
      return;
    }

    loadData();
  }, [disableServerQuery, loadData]);

  // Re-apply the filter (and reset to page 1) when the caller passes a new
  // initialFilterModel after mount (e.g. a URL-driven status filter changes).
  // Compare against the last-applied value (not a "have we run yet" flag) so
  // this stays a no-op if the effect re-fires without the prop actually
  // changing, e.g. React StrictMode's dev-only mount/cleanup/remount cycle.
  useEffect(() => {
    if (initialFilterModel === appliedFilterModelRef.current) {
      return;
    }
    appliedFilterModelRef.current = initialFilterModel;

    const nextFilterModel = initialFilterModel ?? { items: [] };

    setPaginationModel((prevPaginationModel) =>
      prevPaginationModel.page === 0
        ? prevPaginationModel
        : { ...prevPaginationModel, page: 0 }
    );
    setFilterModel(nextFilterModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilterModel]);

  // Same as above, but for extraParams (e.g. technician_id, date range) -
  // these bypass GridFilterModel entirely, so they need their own reset.
  useEffect(() => {
    if (extraParams === appliedExtraParamsRef.current) {
      return;
    }
    appliedExtraParamsRef.current = extraParams;

    setPaginationModel((prevPaginationModel) =>
      prevPaginationModel.page === 0
        ? prevPaginationModel
        : { ...prevPaginationModel, page: 0 }
    );
  }, [extraParams]);

  // The listener below is registered once and never re-subscribes (refreshEventName
  // is a stable string/array), so it must not close over `loadData` directly - that
  // would freeze it on the mount-time queryOptions (e.g. no quick filter applied yet)
  // and every later refresh (like "contractAdded" after creating a contract) would
  // silently refetch with that stale, unfiltered query, undoing the user's quick
  // filter. Route it through a ref that's always kept current instead.
  const loadDataRef = useRef(loadData);
  useEffect(() => {
    loadDataRef.current = loadData;
  }, [loadData]);

  useEffect(() => {
    if (disableServerQuery) {
      return undefined;
    }

    const eventNames = refreshEventName
      ? Array.isArray(refreshEventName)
        ? refreshEventName
        : [refreshEventName]
      : [];

    if (eventNames.length === 0) {
      return undefined;
    }

    const handleRefreshEvent = () => {
      loadDataRef.current();
    };

    eventNames.forEach((eventName) => {
      window.addEventListener(eventName, handleRefreshEvent);
    });

    return () => {
      eventNames.forEach((eventName) => {
        window.removeEventListener(eventName, handleRefreshEvent);
      });
    };
  }, [disableServerQuery, refreshEventName]);

  const rows =
    results?.map((result) => {
      const row: any = { id: result.id, ...result };
      columns.forEach((column) => {
        row[column.field] = result[column.field];
      });
      return row;
    }) || [];

  const { sx: dataGridSx, ...additionalDataGridProps } = dataGridProps ?? {};
  const effectiveRows =
    Array.isArray(additionalDataGridProps.rows)
      ? additionalDataGridProps.rows
      : rows;
  const effectiveRowCount =
    typeof additionalDataGridProps.rowCount === "number"
      ? additionalDataGridProps.rowCount
      : rowCount;
  const effectiveLoading =
    typeof additionalDataGridProps.loading === "boolean"
      ? additionalDataGridProps.loading
      : loading;
  const effectivePaginationModel =
    additionalDataGridProps.paginationModel ?? paginationModel;
  const effectiveSortModel = additionalDataGridProps.sortModel ?? sortModel;
  const effectiveFilterModel = additionalDataGridProps.filterModel ?? filterModel;

  const resolvedColumns = useMemo(
    () =>
      isMediumUp
        ? columns.map((column) => {
            const forcedWidth = column.width;

            if (forcedWidth !== undefined) {
              return {
                ...column,
                flex: undefined,
                width: forcedWidth,
              };
            }

            return {
              ...column,
              flex: column.flex ?? 1,
              width: undefined,
              minWidth: column.minWidth ?? 80,
              maxWidth: column.field === "id" ? 90 : column.maxWidth ?? undefined,
            };
          })
        : columns,
    [columns, isMediumUp]
  );

  const handleProcessRowUpdateError = () => {
    enqueueSnackbar(
      t("Update was not successfull. Please contact support."), ERROR_SNACKBAR);
  };

  const handleRowClick = (params: any) => {
    if (onRowClick) {
      const fullRowData = params.row || results?.find((r) => r.id === params.id);
      onRowClick(params.id, fullRowData);
    }
  };

  const getRowIdProp = additionalDataGridProps.getRowId;
  const resolveRowId = useCallback(
    (row: any): GridRowId => {
      // Preserve the id's original type (e.g. a numeric primary key). The
      // desktop DataGrid hands renderCell/onRowClick the raw `row.id`, so
      // callers legitimately compare it with `===` against their own data
      // (see SendReminder's handleToggle). Coercing to a string here made the
      // mobile list disagree with the desktop grid and silently break those
      // lookups. MobileDataGridList stringifies where it needs a React key.
      if (typeof getRowIdProp === "function") {
        return getRowIdProp(row);
      }

      return row.id;
    },
    [getRowIdProp]
  );

  const handleMobileRowClick = useCallback(
    (rowId: string, row: any) => {
      if (!onRowClick) {
        return;
      }

      onRowClick(rowId, row);
    },
    [onRowClick]
  );

  const selectedRows = rowSelection?.selectedRows;
  // Memoised on the caller's array so the controlled model keeps its identity
  // between renders; DataGrid re-syncs its selection whenever it changes.
  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({
      type: "include",
      ids: new Set((selectedRows ?? []).map(resolveRowId)),
    }),
    [selectedRows, resolveRowId]
  );

  /**
   * Hands the caller the rows behind a new set of selected ids. Rows on the
   * current page supply fresh data; rows ticked on another page keep what was
   * captured when they were selected.
   */
  const commitSelectedIds = (ids: GridRowId[]) => {
    if (!rowSelection) {
      return;
    }

    const rowsById = new Map<GridRowId, any>();
    (selectedRows ?? []).forEach((row) => rowsById.set(resolveRowId(row), row));
    effectiveRows.forEach((row) => rowsById.set(resolveRowId(row), row));

    rowSelection.onChange(
      ids.flatMap((id) => (rowsById.has(id) ? [rowsById.get(id)] : []))
    );
  };

  const handleRowSelectionModelChange = (model: GridRowSelectionModel) => {
    // checkboxSelectionVisibleOnly keeps "select all" to an explicit list of
    // the loaded page's ids; an exclude model is only resolved as a fallback.
    const ids =
      model.type === "include"
        ? Array.from(model.ids)
        : effectiveRows
            .map(resolveRowId)
            .filter((id) => !model.ids.has(id));

    commitSelectedIds(ids);
  };

  const toggleMobileRowSelected = (row: any) => {
    const rowId = resolveRowId(row);
    const ids = Array.from(rowSelectionModel.ids);

    commitSelectedIds(
      rowSelectionModel.ids.has(rowId)
        ? ids.filter((id) => id !== rowId)
        : [...ids, rowId]
    );
  };

  const profileSelectValue = profileSettings.activeProfileId ?? "";

  const canRenderProfileControls = Boolean(gridSettingsKey);
  const canSaveProfile =
    hasUnsavedChanges &&
    Boolean(profileSettings.activeProfileId) &&
    profileSettings.activeProfileId !== LEGACY_PROFILE_ID;
  const canRemoveProfile =
    !hasUnsavedChanges &&
    Boolean(profileSettings.activeProfileId) &&
    profileSettings.activeProfileId !== LEGACY_PROFILE_ID &&
    profileOptions.length > 1;

  const profileControls = (
    <>
      <FormControl size="small" className={styles.profileSelect}>
        <InputLabel>{t("Grid profile")}</InputLabel>
        <Select
          label={t("Grid profile")}
          value={profileSelectValue}
          onChange={handleProfileChange}
        >
          {profileOptions.map((profile) => (
            <MenuItem key={profile.id} value={profile.id}>
              {profile.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        className={styles.profileActionButton}
        size="small"
        onClick={openCreateProfileModal}
        disabled={!hasUnsavedChanges}
        aria-label={t("Add profile")}
      >
        <AddOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton
        className={styles.profileActionButton}
        size="small"
        onClick={handleSaveProfile}
        disabled={!canSaveProfile}
        aria-label={t("Save profile")}
      >
        <SaveOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton
        className={styles.profileActionButton}
        size="small"
        onClick={handleRemoveProfile}
        disabled={!canRemoveProfile}
        aria-label={t("Remove profile")}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </>
  );

  const DataGridFooter = () => (
    <GridFooterContainer>
      <Box className={styles.footerProfileControls}>
        {canRenderProfileControls ? profileControls : null}
      </Box>
      <GridPagination />
    </GridFooterContainer>
  );

  const dataGridSlots = canRenderProfileControls
    ? {
        ...additionalDataGridProps.slots,
        footer: DataGridFooter,
      }
    : additionalDataGridProps.slots;

  const handleSortModelChange = useCallback(
    (nextSortModel: GridSortModel) => {
      setSortModel(nextSortModel);

      if (!gridSettingsKey) {
        return;
      }

      queueDatagridSettingsUpdate((previous) => ({
        ...previous,
        draftSortModel: toGridSortModel(nextSortModel),
      }));
    },
    [gridSettingsKey, queueDatagridSettingsUpdate]
  );

  const handlePaginationModelChange = useCallback(
    (nextPaginationModel: GridPaginationModel) => {
      setPaginationModel(nextPaginationModel);

      if (!gridSettingsKey) {
        return;
      }

      queueDatagridSettingsUpdate((previous) => ({
        ...previous,
        draftPageSize: normalizePageSize(nextPaginationModel.pageSize),
      }));
    },
    [gridSettingsKey, queueDatagridSettingsUpdate]
  );

  const effectivePaginationModelChange =
    additionalDataGridProps.onPaginationModelChange ?? handlePaginationModelChange;
  const effectiveSortModelChange =
    additionalDataGridProps.onSortModelChange ?? handleSortModelChange;
  const effectiveFilterModelChange =
    additionalDataGridProps.onFilterModelChange ?? setFilterModel;
  const handlePaginationModelChangeForMobile = useCallback(
    (model: GridPaginationModel) => {
      effectivePaginationModelChange(model, {} as GridCallbackDetails<"pagination">);
    },
    [effectivePaginationModelChange]
  );
  const handleSortModelChangeForMobile = useCallback(
    (model: GridSortModel) => {
      effectiveSortModelChange(model, {} as GridCallbackDetails<any>);
    },
    [effectiveSortModelChange]
  );
  const handleFilterModelChangeForMobile = useCallback(
    (model: GridFilterModel) => {
      effectiveFilterModelChange(model, {} as GridCallbackDetails<"filter">);
    },
    [effectiveFilterModelChange]
  );

  // While loading we don't yet know how many rows the page will hold, so
  // reserve room for a full page (or the last known row count, whichever is
  // smaller). Once loaded, shrink to fit the rows actually rendered. This keeps
  // the skeleton from rendering short and then growing the layout as data
  // streams in.
  //
  // This dynamic reservation is opt-in: it only kicks in when the caller has
  // pinned a `minHeight` via `dataGridProps.sx` (e.g. the send-reminders modal,
  // where the grid sits in free-flowing modal content and would otherwise jump
  // as rows stream in). The default - every list/workspace view - passes no
  // minHeight, so the grid keeps filling its 100%-height container and scrolls
  // internally; forcing a tall minHeight there makes it overflow the workspace.
  const reservedRowCount = effectiveLoading
    ? Math.min(
        effectivePaginationModel.pageSize,
        effectiveRowCount || effectivePaginationModel.pageSize
      )
    : Math.max(effectiveRows.length, 1);
  const callerMinHeight =
    dataGridSx && typeof (dataGridSx as { minHeight?: unknown }).minHeight === "number"
      ? ((dataGridSx as { minHeight: number }).minHeight)
      : 0;
  const reservedMinHeight =
    callerMinHeight > 0
      ? Math.max(
          GRID_CHROME_HEIGHT + reservedRowCount * APPROX_ROW_HEIGHT,
          callerMinHeight
        )
      : undefined;

  const dataGridPageSizeOptions = useMemo(() => {
    const baseOptions = additionalDataGridProps.pageSizeOptions ?? [10, 25, 50, 100];
    const normalizedOptions = baseOptions.filter(
      (option): option is number => typeof option === "number" && option > 0
    );

    return normalizedOptions.includes(effectivePaginationModel.pageSize)
      ? normalizedOptions
      : [...normalizedOptions, effectivePaginationModel.pageSize].sort((left, right) => left - right);
  }, [additionalDataGridProps.pageSizeOptions, effectivePaginationModel.pageSize]);

  return (
    <Box className={styles.container}>
      {title && (
        <Box className={isMediumUp ? styles.header : styles.mobileHeader}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
            {icon && <Box className={styles.iconBox}>{icon}</Box>}
            <Box sx={{ display: "flex" }}>{title}</Box>
          </Box>
          <Box className={styles.headerControls}>
            {headerActions && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  flexShrink: 1,
                  minWidth: 0,
                  maxWidth: "min(50vw, 900px)",
                  ml: 1,
                  overflowX: "auto",
                  overflowY: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {headerActions}
              </Box>
            )}
          </Box>
        </Box>
      )}
      {isMediumUp ? (
        <DataGrid
          rows={effectiveRows}
          columns={resolvedColumns}
          pageSizeOptions={dataGridPageSizeOptions}
          {...additionalDataGridProps}
          {...(rowSelection
            ? {
                checkboxSelection: true,
                checkboxSelectionVisibleOnly: true,
                // A row click still opens the record; only the checkbox selects.
                disableRowSelectionOnClick: true,
                // Server-side paging: without this, moving to another page
                // would drop everything ticked on the previous one.
                keepNonExistentRowsSelected: true,
                rowSelectionModel,
                onRowSelectionModelChange: handleRowSelectionModelChange,
              }
            : {})}
          sx={{
            border: 0,
            color: "var(--textColor)",
            backgroundColor: "var(--dataGridBg)",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "var(--dataGridHeaderBg)",
              borderBottom: `1px solid var(--dataGridBorderColor)`,
              color: "var(--textColor)",
              boxShadow: "none",
            },
            "& .MuiDataGrid-columnHeadersInner": {
              backgroundColor: "var(--dataGridHeaderBg)",
            },
            "& .MuiDataGrid-columnHeadersVirtualScroller": {
              backgroundColor: "var(--dataGridHeaderBg)",
            },
            "& .MuiDataGrid-columnHeadersVirtualScrollerContent": {
              backgroundColor: "var(--dataGridHeaderBg)",
            },
            "& .MuiDataGrid-columnSeparator": {
              color: "var(--dataGridBorderColor)",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "var(--textColor)",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "var(--dataGridHeaderBg)",
            },
            "& .MuiDataGrid-cell": {
              minHeight: "56px !important",
              cursor: "pointer",
              borderColor: "var(--dataGridBorderColor)",
              color: "var(--textColor)",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            // Let chip text wrap onto multiple lines instead of being clipped
            // with an ellipsis - grid rows already auto-size to their content.
            "& .MuiDataGrid-cell .MuiChip-root": {
              height: "auto",
              minHeight: "24px",
              padding: "3px 0",
            },
            "& .MuiDataGrid-cell .MuiChip-label": {
              whiteSpace: "normal",
              overflow: "visible",
              textOverflow: "clip",
              wordBreak: "break-word",
              lineHeight: 1.3,
            },
            "& .MuiDataGrid-row": {
              transition: "background-color 0.2s ease",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "var(--dataGridRowHover)",
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "var(--dataGridRowSelected)",
              "&:hover": {
                backgroundColor: "var(--dataGridRowSelected)",
              },
            },
            "& .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
              backgroundColor: "var(--dataGridHeaderBg)",
            },
            "& .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller": {
              backgroundColor: "var(--dataGridHeaderBg)",
            },
            "& .MuiDataGrid-filler": {
              backgroundColor: "var(--dataGridBg)",
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: "var(--dataGridBg)",
            },
            "& .MuiDataGrid-overlay": {
              backgroundColor: "var(--dataGridBg)",
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: "var(--dataGridFooterBg)",
              borderTop: `1px solid var(--dataGridBorderColor)`,
              color: "var(--textColor)",
            },
            "& .MuiDataGrid-toolbarContainer": {
              backgroundColor: "var(--dataGridToolbarBg)",
              borderBottom: `1px solid var(--dataGridBorderColor)`,
              color: "var(--textColor)",
            },
            "& .MuiTablePagination-root": {
              color: "var(--textColor)",
            },
            "& .MuiIconButton-root": {
              color: "var(--iconColor)",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "var(--dataGridBg)",
              "&::-webkit-scrollbar": {
                height: 10,
                width: 10,
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
                margin: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "var(--dataGridScrollbarThumb)",
                borderRadius: "var(--defaultRadius)",
                border: "2px solid transparent",
                backgroundClip: "padding-box",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "var(--dataGridScrollbarThumbHover)",
              },
            },
            "& .MuiDataGrid-scrollbar": {
              scrollbarColor: "var(--dataGridScrollbarThumb) transparent",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                height: 10,
                width: 10,
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
                margin: 4,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "var(--dataGridScrollbarThumb)",
                borderRadius: "var(--defaultRadius)",
                border: "2px solid transparent",
                backgroundClip: "padding-box",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "var(--dataGridScrollbarThumbHover)",
              },
            },
            "& .MuiDataGrid-withBorderColor": {
              borderColor: "var(--dataGridBorderColor)",
            },
            "@media (min-width:900px)": {
              "& .MuiDataGrid-main": {
                overflowX: "hidden",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "hidden",
              },
              "& .MuiDataGrid-cell": {
                minHeight: "56px !important",
                whiteSpace: "normal",
                wordBreak: "break-word",
                alignItems: "flex-start",
                lineHeight: "1.4",
                paddingTop: "8px",
                paddingBottom: "8px",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: "1.3",
              },
            },
            ...dataGridSx,
            ...(reservedMinHeight !== undefined
              ? { minHeight: reservedMinHeight }
              : {}),
          }}
          rowCount={effectiveRowCount}
          loading={effectiveLoading}
          paginationModel={effectivePaginationModel}
          sortModel={effectiveSortModel}
          filterModel={effectiveFilterModel}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          onPaginationModelChange={effectivePaginationModelChange}
          onSortModelChange={effectiveSortModelChange}
          onFilterModelChange={effectiveFilterModelChange}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          onRowClick={handleRowClick}
          getRowHeight={() => "auto"}
          disableColumnFilter={additionalDataGridProps.disableColumnFilter ?? false}
          columnVisibilityModel={columnVisibilityModel}
          onColumnWidthChange={handleColumnWidthChange}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          disableColumnSelector={false}
          showToolbar
          slots={dataGridSlots}
        />
      ) : (
        <MobileDataGridList
          rows={effectiveRows.map((row) => ({ ...row, id: resolveRowId(row) }))}
          columns={columns}
          loading={effectiveLoading}
          rowCount={effectiveRowCount}
          paginationModel={effectivePaginationModel}
          onPaginationModelChange={handlePaginationModelChangeForMobile}
          sortModel={effectiveSortModel}
          onSortModelChange={handleSortModelChangeForMobile}
          filterModel={effectiveFilterModel}
          onFilterModelChange={handleFilterModelChangeForMobile}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          storedColumnSettings={
            canRenderProfileControls
              ? profileSettings.draftColumns
              : undefined
          }
          getRowClassName={additionalDataGridProps.getRowClassName}
          onRowClick={handleMobileRowClick}
          selectedRowIds={
            rowSelection
              ? new Set(Array.from(rowSelectionModel.ids).map(String))
              : undefined
          }
          onToggleRowSelected={rowSelection ? toggleMobileRowSelected : undefined}
        />
      )}
      {canRenderProfileControls && !isMediumUp && (
        <Box className={styles.mobileProfileControls}>{profileControls}</Box>
      )}
      <CustomModal
        opened={createProfileModalOpen}
        setOpened={setCreateProfileModalOpen}
        title={t("Create grid profile")}
        width={420}
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("Profile name")}
              value={profileNameInput}
              onChange={(event) => setProfileNameInput(event.target.value)}
              autoFocus
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                variant="text"
                onClick={() => setCreateProfileModalOpen(false)}
              >
                {t("Cancel")}
              </Button>
              <Button variant="contained" onClick={handleCreateProfile}>
                {t("Create profile")}
              </Button>
            </Box>
          </Box>
        }
      />
    </Box>
  );
}
